import type { JSX } from 'preact';

// Line icons ported from app/beercup/project/components/tokens.jsx. `size` and
// `color` default to inheriting the current text colour so they tint via CSS.
type P = { size?: number; color?: string };
const svg = (size: number, children: JSX.Element): JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {children}
  </svg>
);

export const Icon = {
  user: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <>
      <circle cx="12" cy="8" r="4" stroke={color} stroke-width="2" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke={color} stroke-width="2" stroke-linecap="round" />
    </>),
  ball: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <>
      <circle cx="12" cy="12" r="9" stroke={color} stroke-width="2" />
      <path d="M12 3l3 6-3 3-3-3 3-6zM3 12l6 0M15 12l6 0M12 21l3-6M12 21l-3-6" stroke={color} stroke-width="1.5" />
    </>),
  trophy: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M7 4h10v4a5 5 0 01-10 0V4zM5 5H3v2a3 3 0 003 3M19 5h2v2a3 3 0 01-3 3M10 14h4v3h-4zM8 20h8" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />),
  info: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <>
      <circle cx="12" cy="12" r="9" stroke={color} stroke-width="2" />
      <path d="M12 11v5M12 8h.01" stroke={color} stroke-width="2" stroke-linecap="round" />
    </>),
  pin: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <>
      <path d="M12 21s7-5.5 7-11a7 7 0 10-14 0c0 5.5 7 11 7 11z" stroke={color} stroke-width="2" stroke-linejoin="round" />
      <circle cx="12" cy="10" r="2.5" stroke={color} stroke-width="2" />
    </>),
  boost: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" stroke={color} stroke-width="2" stroke-linejoin="round" />),
  chat: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M4 5h16v11H8l-4 4V5z" stroke={color} stroke-width="2" stroke-linejoin="round" />),
  chevron: ({ size = 16, color = 'currentColor' }: P) =>
    svg(size, <path d="M9 5l7 7-7 7" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />),
  check: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M5 12l5 5L20 6" stroke={color} stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />),
  settings: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <>
      <circle cx="12" cy="12" r="3" stroke={color} stroke-width="2" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8 2 2 0 11-2.8 2.8 1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5 2 2 0 11-4 0 1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3 2 2 0 11-2.8-2.8 1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1 2 2 0 110-4 1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8 2 2 0 112.8-2.8 1.7 1.7 0 001.8.3 1.7 1.7 0 001-1.5 2 2 0 114 0 1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3 2 2 0 112.8 2.8 1.7 1.7 0 00-.3 1.8 1.7 1.7 0 001.5 1 2 2 0 110 4 1.7 1.7 0 00-1.5 1z" stroke={color} stroke-width="1.5" />
    </>),
  logout: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M14 8V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h7a2 2 0 002-2v-2M9 12h12m0 0l-3-3m3 3l-3 3" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />),
  menu: ({ size = 22, color = 'currentColor' }: P) =>
    svg(size, <path d="M4 7h16M4 12h16M4 17h16" stroke={color} stroke-width="2" stroke-linecap="round" />),
  close: ({ size = 22, color = 'currentColor' }: P) =>
    svg(size, <path d="M6 6l12 12M18 6L6 18" stroke={color} stroke-width="2" stroke-linecap="round" />),
  flame: ({ size = 16, color = 'currentColor' }: P) =>
    svg(size, <path d="M12 2s5 4 5 10a5 5 0 01-10 0c0-2 1-3 1-3s-1 1-1 3a3 3 0 003 3 3 3 0 003-3c0-3-1-5-1-5s-2 3-4 4c0-4 4-7 4-9z" stroke={color} stroke-width="1.6" stroke-linejoin="round" />),
  plus: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M12 5v14M5 12h14" stroke={color} stroke-width="2.2" stroke-linecap="round" />),
  back: ({ size = 20, color = 'currentColor' }: P) =>
    svg(size, <path d="M15 5l-7 7 7 7" stroke={color} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />),
};
